import type { GeoFormat } from "@rogoag/shared-ts";
import type { FeatureCollection } from "geojson";

const EXTENSION_FORMAT_MAP: Record<string, GeoFormat> = {
	".geojson": "GeoJSON",
	".json": "GeoJSON",
	".zip": "SHP",
	".shp": "SHP",
	".kml": "KML",
	".kmz": "KMZ",
};

/** Detect the geo format from a file's extension. Returns null if unsupported. */
export function detectFormatFromFile(file: File): GeoFormat | null {
	const name = file.name.toLowerCase();
	const dotIndex = name.lastIndexOf(".");
	if (dotIndex === -1) return null;
	const ext = name.slice(dotIndex);
	return EXTENSION_FORMAT_MAP[ext] ?? null;
}

/** Extract metadata from a GeoJSON FeatureCollection. */
export function analyzeGeoJSON(geojson: FeatureCollection): {
	featureCount: number;
	geometryTypes: string[];
	propertyKeys: string[];
} {
	const features = geojson.features ?? [];
	const featureCount = features.length;

	const geometryTypeSet = new Set<string>();
	const propertyKeySet = new Set<string>();

	for (const feature of features) {
		if (feature.geometry?.type) {
			geometryTypeSet.add(feature.geometry.type);
		}
		if (feature.properties) {
			for (const key of Object.keys(feature.properties)) {
				propertyKeySet.add(key);
			}
		}
	}

	return {
		featureCount,
		geometryTypes: Array.from(geometryTypeSet),
		propertyKeys: Array.from(propertyKeySet),
	};
}

const FORMAT_EXTENSION_MAP: Partial<Record<GeoFormat, string>> = {
	SHP: ".zip",
	GeoJSON: ".json",
	KML: ".kml",
	KMZ: ".kmz",
};

/** Generate an output filename based on the input file and target format. */
export function generateOutputFilename(inputFile: File | null, targetFormat: GeoFormat): string {
	const baseName = inputFile ? inputFile.name.replace(/\.[^.]+$/, "") : "converted";
	return baseName + (FORMAT_EXTENSION_MAP[targetFormat] ?? `.${targetFormat.toLowerCase()}`);
}
